/**
 * Shell global con header sticky + nav + footer (Sprint 7 C).
 *
 * Se monta solo cuando hay session full. El portal publico
 * (/portal/:token) y el flujo de login no la usan.
 *
 * Sprint BB — extensiones:
 *   - AppShellContext con setBackTo / setBreadcrumb (BB-UX-01, BB-UX-03).
 *   - Prop `variant?: 'tenant' | 'platform'` para modo super_admin (BB-PLATFORM-01).
 *   - Back button con ChevronLeft de Lucide (BB-UX-01).
 *   - Breadcrumb debajo del header cuando hay items (BB-UX-03).
 *
 * Por qué contexto en lugar de props directas: el AppShell envuelve toda la
 * app en App.tsx. Las rutas hijas no pueden pasar props al shell directamente.
 * Cada página llama useEffect([setBackTo, deps]) para declarar su navegación
 * contextual y limpia al desmontar. Este patrón ya existe en AuthContext.
 */
import { createContext, type ReactNode, useContext, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

import { Logo } from './Logo.js';
import { Button } from './ui/Button.js';
import { useAuth } from '../auth/AuthContext.js';
import { PLATFORM_TABS } from '../pages/platform-tabs.js';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

export interface BackTo {
  href: string;
  label: string;
}

export interface BreadcrumbItem {
  label: string;
  /** Si ausente, el item es el último (current page) — se renderiza como texto. */
  href?: string;
}

interface AppShellContextValue {
  backTo: BackTo | null;
  setBackTo: (b: BackTo | null) => void;
  breadcrumb: BreadcrumbItem[];
  setBreadcrumb: (items: BreadcrumbItem[]) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Contexto
// ─────────────────────────────────────────────────────────────────────────────

const AppShellContext = createContext<AppShellContextValue | null>(null);

// No-op singleton para cuando el hook se usa fuera del AppShell
// (principalmente en tests de páginas individuales que no montan el shell completo).
const NO_OP_CONTEXT: AppShellContextValue = {
  backTo: null,
  setBackTo: () => undefined,
  breadcrumb: [],
  setBreadcrumb: () => undefined,
};

/**
 * Hook que expone el contexto del shell.
 *
 * En producción siempre vive dentro de AppShell; el no-op context previene
 * crashes en tests de páginas que no montan el shell completo.
 */
export function useAppShell(): AppShellContextValue {
  const ctx = useContext(AppShellContext);
  // Fallback no-op para tests donde la página se monta sin AppShell.
  // En producción el AppShell envuelve toda la app (App.tsx), por lo que
  // este fallback nunca se activa en runtime real.
  return ctx ?? NO_OP_CONTEXT;
}

// ─────────────────────────────────────────────────────────────────────────────
// Props del shell
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  children: ReactNode;
  /**
   * variant='tenant' (default): nav estándar de tenant (Clientes, Hallazgos…).
   * variant='platform': nav super_admin (Tenants, Usuarios), badge "PLATAFORMA".
   * App.tsx elige según location.pathname.startsWith('/platform/').
   * undefined se trata como 'tenant' (mismo default).
   */
  variant?: 'tenant' | 'platform' | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

export function AppShell({ children, variant = 'tenant' }: Props): JSX.Element {
  const { session, clear } = useAuth();
  const location = useLocation();

  const [backTo, setBackTo] = useState<BackTo | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);

  // No envolvemos rutas publicas / de auth-flow.
  const isAuthFlow =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/mfa/') ||
    location.pathname.startsWith('/portal/') ||
    location.pathname.startsWith('/capture/');
  if (isAuthFlow || session?.kind !== 'full') {
    return (
      <AppShellContext.Provider value={{ backTo, setBackTo, breadcrumb, setBreadcrumb }}>
        {children}
      </AppShellContext.Provider>
    );
  }

  const isAdmin = session.user?.role === 'admin';
  const isSuperAdmin = session.user?.role === 'super_admin';
  const isPlatformVariant = variant === 'platform';

  return (
    <AppShellContext.Provider value={{ backTo, setBackTo, breadcrumb, setBreadcrumb }}>
      <div className="min-h-screen flex flex-col bg-slate-50">
        {/* bg-cumple-dark aplica el gradiente oscuro (#0F172A → #1E1B4B) del kit
            Option 4. Texto en blanco para contraste AA sobre fondo oscuro. */}
        <header className="sticky top-0 z-30 bg-cumple-dark border-b border-white/10">
          <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Link
                to={isPlatformVariant ? '/platform/tenants' : '/clients'}
                aria-label="Cumple — inicio"
                className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cumple-primary-900 rounded-sm"
              >
                <Logo iconHeight={28} tone="inverse" ariaLabel="" />
              </Link>

              {/* Badge PLATAFORMA — solo en variant platform (BB-PLATFORM-01) */}
              {isPlatformVariant && (
                <span
                  className="ml-2 inline-block bg-cumple-danger-600 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                  aria-label="Modo plataforma"
                >
                  PLATAFORMA
                </span>
              )}

              {/* Back button (BB-UX-01): visible cuando backTo está seteado */}
              {backTo !== null && (
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="text-white/80 hover:bg-white/10 hover:text-white border-transparent"
                >
                  <Link to={backTo.href} aria-label={`Volver a ${backTo.label}`}>
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                    <span className="hidden sm:inline">{backTo.label}</span>
                  </Link>
                </Button>
              )}
            </div>

            {/* Nav principal — se adapta según variant */}
            <nav aria-label="Principal" className="hidden sm:flex items-center gap-1">
              {isPlatformVariant ? (
                // Nav super_admin: todos los tabs desde la constante compartida (ADR-038)
                <>
                  {PLATFORM_TABS.map((tab) => (
                    <NavItem key={tab.key} to={tab.to}>
                      {tab.label}
                    </NavItem>
                  ))}
                </>
              ) : (
                // Nav tenant estándar
                <>
                  <NavItem to="/clients">Clientes</NavItem>
                  <NavItem to="/findings">Hallazgos</NavItem>
                  <NavItem to="/sync">Sincronizacion</NavItem>
                  {(isAdmin || isSuperAdmin) && (
                    <>
                      <NavItem to="/admin/dashboard">Dashboard</NavItem>
                      <NavItem to="/admin/insights">Insights</NavItem>
                      <NavItem to="/admin/surveys">Plantillas</NavItem>
                      <NavItem to="/admin/users">Usuarios</NavItem>
                    </>
                  )}
                </>
              )}
            </nav>

            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-xs text-white/60" aria-label="Usuario">
                {session.user?.email ?? '—'}
              </span>
              <button
                type="button"
                onClick={clear}
                className="rounded-md border border-white/20 px-3 py-1 text-xs font-medium text-white/80 hover:bg-white/10"
              >
                Salir
              </button>
            </div>
          </div>

          {/* Breadcrumb (BB-UX-03): segunda fila, solo si hay items */}
          {breadcrumb.length > 0 && (
            <div className="mx-auto max-w-6xl px-4 py-1.5 border-t border-white/10">
              <BreadcrumbNav items={breadcrumb} />
            </div>
          )}

          {/* Mobile nav */}
          <details className="sm:hidden border-t border-white/10">
            <summary className="px-4 py-2 text-xs text-white/70 cursor-pointer">Menu</summary>
            <nav aria-label="Principal mobile" className="px-4 py-2 flex flex-col gap-1">
              {isPlatformVariant ? (
                // Nav super_admin: todos los tabs desde la constante compartida (ADR-038)
                <>
                  {PLATFORM_TABS.map((tab) => (
                    <NavItem key={tab.key} to={tab.to}>
                      {tab.label}
                    </NavItem>
                  ))}
                </>
              ) : (
                <>
                  <NavItem to="/clients">Clientes</NavItem>
                  <NavItem to="/findings">Hallazgos</NavItem>
                  <NavItem to="/sync">Sincronizacion</NavItem>
                  {(isAdmin || isSuperAdmin) && (
                    <>
                      <NavItem to="/admin/dashboard">Dashboard</NavItem>
                      <NavItem to="/admin/insights">Insights</NavItem>
                      <NavItem to="/admin/surveys">Plantillas</NavItem>
                      <NavItem to="/admin/users">Usuarios</NavItem>
                    </>
                  )}
                </>
              )}
            </nav>
          </details>
        </header>

        <div className="flex-1">{children}</div>

        <footer className="border-t border-slate-200 bg-white px-4 py-3 text-center text-[10px] text-slate-400">
          {isPlatformVariant
            ? 'Cumple Platform · Acceso restringido · MSSPRD'
            : 'Cumple · Auditoría y captura de evidencias · un producto de MSSPRD'}
        </footer>
      </div>
    </AppShellContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponentes internos
// ─────────────────────────────────────────────────────────────────────────────

function NavItem({ to, children }: { to: string; children: ReactNode }): JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        'rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' +
        // Activo: bg blanco translúcido + texto blanco (AA sobre bg-cumple-dark).
        // Inactivo: texto blanco/70 con hover blanco/10 de fondo.
        (isActive ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white')
      }
    >
      {children}
    </NavLink>
  );
}

/** Breadcrumb inline — solo usado dentro del AppShell (BB-UX-03). */
function BreadcrumbNav({ items }: { items: BreadcrumbItem[] }): JSX.Element {
  return (
    <nav aria-label="Ruta de navegación">
      <ol className="flex flex-wrap gap-1.5 text-xs text-white/70">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1.5">
            {i > 0 && <span aria-hidden="true">›</span>}
            {item.href !== undefined ? (
              <Link
                to={item.href}
                className="hover:text-white transition-colors"
                title={item.label.length > 20 ? item.label : undefined}
              >
                {item.label.length > 20 ? `${item.label.slice(0, 20)}…` : item.label}
              </Link>
            ) : (
              <span
                className="text-white"
                title={item.label.length > 20 ? item.label : undefined}
                aria-current="page"
              >
                {item.label.length > 20 ? `${item.label.slice(0, 20)}…` : item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
